/**
 * Agent evaluation shared contracts.
 * @module agent-eval-result
 * @see docs/project/conventions.md
 */
export type AgentEvalGrade = {
  grader: string;
  kind: 'script' | 'ai-judge';
  pass: boolean;
  score: number | null; // null is a grader error, never a verdict
  error: string | null;
  scale: 'binary' | '1-5' | 'script';
  reason: string;
  judgeModel: string | null;
  costCents: number | null; // judge cost only; scripts have no model cost
};
/** One message of a multi-turn conversation; the API's server-side copy is the graded one. */
export type AgentEvalTranscriptEntry = { role: 'user' | 'agent'; text: string };
export type AgentEvalTokens = {
  input: number;
  output: number;
  cacheRead: number;
  cacheCreation: number;
};
export type AgentEvalAttempt = {
  index: number;
  status: 'completed' | 'error';
  error: string | null;
  passed: boolean;
  exitCode: number | null;
  timedOut: boolean;
  durationMs: number;
  llmLatencyMs: number;
  llmCalls: number;
  turns: number | null; // multi-turn only
  transcript: AgentEvalTranscriptEntry[] | null; // multi-turn only, present only with `eval get --transcript`
  userSimCostCents: number | null; // never part of costCents; every agent metric above (llmCalls, llmLatencyMs, calls-derived tokens, costCents, costComplete, and the summary's unpricedCalls/incompleteCalls) counts `role = 'agent'` rows only
  calls: {
    role: 'agent' | 'user-sim';
    wire: 'anthropic' | 'openai';
    model: string;
    streamed: boolean;
    complete: boolean;
    tokens: AgentEvalTokens;
    costCents: number | null;
  }[];
  tokens: AgentEvalTokens;
  costCents: number | null;
  costComplete: boolean;
  grades: AgentEvalGrade[];
};
export type AgentEvalCaseResult = {
  key: string;
  title: string;
  passed: boolean;
  allPassed: boolean;
  passedAttempts: number;
  attempts: AgentEvalAttempt[]; // allPassed is pass^k: every attempt passed
  meanScore: number;
  costCentsPerAttempt: number | null;
  durationMsPerAttempt: number;
  tokensPerAttempt: AgentEvalTokens;
};
export type AgentEvalRunResult = {
  id: string;
  batchId: string;
  eval: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  verdict: 'pass' | 'fail' | null;
  error: string | null;
  model: { alias: string; provider: 'anthropic' | 'azure'; id: string };
  commit: string;
  casesDigest: string;
  repeats: number;
  passAtLeast: number;
  passBar: number;
  summary: {
    cases: number;
    casesPassed: number;
    passRate: number;
    attempts: number;
    attemptsPassed: number;
    infraFailures: number;
    meanScore: number;
    tokens: AgentEvalTokens;
    costCents: number | null;
    costCentsPerTask: number | null;
    unpricedCalls: number;
    incompleteCalls: number;
    durationMsPerTask: number;
    judge: { calls: number; costCents: number | null };
    userSim: { calls: number; costCents: number | null } | null; // multi-turn only; null cost = an unpriced simulator model (a warning), which never nulls the agent's costCents
  };
  cases: AgentEvalCaseResult[];
  warnings: string[];
};

/** Server-built comparison; JSON consumers use runs, markdown uses the case grid. */
export type AgentEvalCompareResult = {
  eval: string;
  commit: string;
  casesDigest: string;
  batchId: string | null;
  runs: AgentEvalRunResult[];
  cases: { key: string; models: { runId: string; result: AgentEvalCaseResult }[] }[];
};

/**
 * A pass rate or bar as a whole percent, floored so a run just under its bar never reads as
 * meeting it (49.6% against a 50% bar prints 49%). One rule for the proof check and the proof page.
 */
export const formatPassPercent = (fraction: number): string =>
  `${Math.floor(Number((fraction * 100).toFixed(6)))}%`;
